/**
 * «Воронка продаж: где теряем» — герой аналитики.
 * Ступенчатые бары по 7 sales-стадиям с числом сделок и % проходящих дальше;
 * самый узкий стык подсвечен красным «тут теряем больше всего».
 * Рядом — конверсия в договор, выиграно/проиграно и причины проигрышей.
 */

import { AlertTriangle, TrendingDown, Trophy, XCircle } from 'lucide-react'
import { BarList, ProgressRing } from './primitives'
import type { BarRow } from './primitives'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatRub } from '@/lib/utils'
import type { DashboardFunnel } from '@/types'

function FunnelSteps({ funnel }: { funnel: DashboardFunnel }) {
  const max = Math.max(1, ...funnel.stages.map((s) => s.count))

  return (
    <div className="space-y-2">
      {funnel.stages.map((s, i) => {
        const pct = Math.round((s.count / max) * 100)
        const next = funnel.stages[i + 1]
        // % проходящих дальше (от текущей к следующей)
        const passRate = next && s.count > 0 ? Math.round((next.count / s.count) * 100) : null
        return (
          <div key={s.stage_key}>
            <div
              className={cn(
                'relative overflow-hidden rounded-md border',
                s.is_bottleneck ? 'border-red-300 bg-red-50' : 'border-border bg-card',
              )}
            >
              <div
                className={cn(
                  'absolute inset-y-0 left-0 transition-all',
                  s.is_bottleneck ? 'bg-red-200/60' : 'bg-primary/15',
                )}
                style={{ width: `${Math.max(pct, 4)}%` }}
              />
              <div className="relative flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate text-sm font-medium">{s.stage_name}</span>
                  {s.is_bottleneck && (
                    <span className="flex items-center gap-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      <AlertTriangle className="h-3 w-3" />
                      тут теряем больше всего
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3 text-sm tabular-nums">
                  <span className="font-bold">{s.count}</span>
                  {s.value > 0 && (
                    <span className="hidden text-xs text-muted-foreground sm:inline">{formatRub(s.value)}</span>
                  )}
                </div>
              </div>
            </div>
            {passRate != null && (
              <div className="flex items-center justify-center py-0.5">
                <span
                  className={cn(
                    'text-[11px] tabular-nums',
                    s.is_bottleneck ? 'font-semibold text-red-600' : 'text-muted-foreground',
                  )}
                >
                  ↓ дальше проходит {passRate}%
                </span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function SalesFunnel({ funnel }: { funnel: DashboardFunnel }) {
  const conv = funnel.conversion_to_contract
  const lostRows: BarRow[] = funnel.lost_reasons.map((r) => ({
    label: r.reason,
    value: r.count,
    accent: 'red' as const,
  }))

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Воронка — занимает 2 колонки */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
            Воронка продаж: где теряем
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FunnelSteps funnel={funnel} />
        </CardContent>
      </Card>

      {/* Правая колонка: конверсия + W/L + причины */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Конверсия в договор</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-3">
            <ProgressRing
              value={conv}
              label={`${Math.round(conv * 100)}%`}
              accent={conv >= 0.5 ? 'green' : conv >= 0.3 ? 'amber' : 'red'}
              caption="из квалифицированных доходят до договора"
            />
            <div className="grid w-full grid-cols-2 gap-2">
              <div className="rounded-lg border border-green-200 bg-green-50 p-2 text-center">
                <Trophy className="mx-auto mb-1 h-4 w-4 text-green-600" />
                <p className="text-lg font-bold tabular-nums text-green-700">{funnel.won_count}</p>
                <p className="text-[11px] text-muted-foreground">выиграно</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center">
                <XCircle className="mx-auto mb-1 h-4 w-4 text-slate-500" />
                <p className="text-lg font-bold tabular-nums text-slate-700">{funnel.lost_count}</p>
                <p className="text-[11px] text-muted-foreground">проиграно</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Причины проигрышей</CardTitle>
          </CardHeader>
          <CardContent>
            {lostRows.length === 0 ? (
              <p className="py-2 text-center text-sm text-muted-foreground">Проигрышей пока нет</p>
            ) : (
              <BarList rows={lostRows} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
